<template>
  <b-card class="mb-2 analytics-table-card">
    <template #header>
      <div class="section-header">
        <div class="d-flex align-items-center">
          <feather-icon
            icon="StarIcon"
            size="16"
            class="mr-75 text-primary"
          />
          <h6 class="card-title mb-0">
            Popular Products
          </h6>
        </div>
        <span class="badge-count">{{ popularProducts.length }} products</span>
        <download-menu
          filename="popular_products"
          :rows="rankedProducts"
          :columns="downloadColumns"
        />
      </div>
    </template>

    <b-table
      :items="rankedProducts"
      :fields="fields"
      sticky-header="420px"
      small
      hover
      show-empty
      empty-text="No product data for the selected period"
      class="analytics-table mb-0"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
    >
      <template #cell(rank)="{ item }">
        <span
          class="rank-badge"
          :class="item._rank <= 3 ? `rank-top-${item._rank}` : ''"
        >
          {{ item._rank }}
        </span>
      </template>

      <template #cell(productName)="{ item }">
        <span class="font-weight-semibold">{{ item.productName }}</span>
      </template>

      <template #cell(styleCode)="{ item }">
        <span class="text-muted small">{{ item.styleCode }}</span>
      </template>

      <template #cell(orders)="{ item }">
        <span class="font-weight-semibold">{{ Number(item.orders || 0).toLocaleString() }}</span>
      </template>

      <template #cell(quantity)="{ item }">
        <span class="font-weight-semibold">{{ Number(item.quantity || 0).toLocaleString() }}</span>
      </template>

      <template #cell(totalSales)="{ item }">
        <span class="font-weight-semibold text-nowrap">
          {{ formatCurrency(item.totalSales || 0) }}
        </span>
      </template>

      <template #cell(action)="{ item }">
        <b-link
          :to="{ name: 'product-detail', params: { id: item.productId, collectionId: item.collectionId } }"
          class="view-link"
        >
          View product
        </b-link>
      </template>
    </b-table>
  </b-card>
</template>

<script>
import { BCard, BTable, BLink } from 'bootstrap-vue'
import DownloadMenu from './DownloadMenu.vue'
import { formatCurrency } from '../analyticsFormat'

export default {
  name: 'AnalyticsPopularProducts',
  components: {
 BCard, BTable, BLink, DownloadMenu,
},
  props: {
    popularProducts: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      sortBy: 'quantity',
      sortDesc: true,
      fields: [
        {
 key: 'rank', label: '#', sortable: false, thClass: 'table-col-header', tdClass: 'text-center', thStyle: { width: '40px' },
},
        {
 key: 'productName', label: 'Name', sortable: true, thClass: 'table-col-header',
},
        {
 key: 'styleCode', label: 'Style Code', sortable: true, thClass: 'table-col-header',
},
        {
 key: 'collectionName', label: 'Collection', sortable: false, thClass: 'table-col-header',
},
        {
 key: 'orders', label: 'Orders', sortable: true, thClass: 'table-col-header text-right', tdClass: 'text-right',
},
        {
 key: 'quantity', label: 'Quantity', sortable: true, thClass: 'table-col-header text-right', tdClass: 'text-right',
},
        {
 key: 'totalSales', label: 'Total', sortable: true, thClass: 'table-col-header text-right', tdClass: 'text-right',
},
        {
 key: 'action', label: 'Actions', thClass: 'table-col-header',
},
      ],
    }
  },
  computed: {
    rankedProducts() {
      return this.popularProducts.map((p, i) => ({ ...p, _rank: i + 1 }))
    },
    downloadColumns() {
      const fmt = n => new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0)
      return [
        { key: '_rank', label: '#' },
        { key: 'productName', label: 'Name' },
        { key: 'styleCode', label: 'Style Code' },
        { key: 'collectionName', label: 'Collection' },
        { key: 'orders', label: 'Orders' },
        { key: 'quantity', label: 'Quantity' },
        { key: 'totalSales', label: 'Total', format: v => fmt(v) },
      ]
    },
  },
  methods: {
    formatCurrency,
  },
}
</script>

<style lang="scss" scoped>
@import './analytics-table';

.rank-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  font-size: 0.7rem;
  font-weight: 700;
  background: #f3f4f6;
  color: #6b7280;

  &.rank-top-1 { background: #fef3c7; color: #d97706; }
  &.rank-top-2 { background: #f1f5f9; color: #475569; }
  &.rank-top-3 { background: #fde8d0; color: #c2410c; }
}

.view-link {
  font-size: 0.75rem;
  font-weight: 600;
  color: #1e40af;
  white-space: nowrap;
  transition: color 0.15s ease;

  &:hover { color: #1d4ed8; text-decoration: underline; }
}
</style>
